<template>
  <div :class="['root', size === 'small' && 'root--small']">
    <div class="square">
      <div class="area area--double">
        <div class="piece piece--black piece--ghost"></div>
        <div
          class="piece piece--black piece--whole"
          :style="pieceStyles[0]"
        ></div>
      </div>
      <div class="area">
        <div class="piece piece--black piece--ghost"></div>
        <div
          class="piece piece--black piece--whole"
          :style="pieceStyles[1]"
        ></div>
      </div>
      <div class="area">
        <div class="piece piece--gold piece--ghost"></div>
        <div
          class="piece piece--gold piece--whole"
          :style="pieceStyles[2]"
        ></div>
      </div>
    </div>

    <div v-if="!hideText" class="text">
      <slot> Loading... </slot>
    </div>
  </div>
</template>

<script>
const initialPieceStyles = [
  {
    opacity: '0',
    transform: 'scaleY(0)',
    transformOrigin: 'bottom',
    transition: 'none',
  },
  {
    opacity: '0',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'none',
  },
  {
    opacity: '0',
    transform: 'scaleY(0)',
    transformOrigin: 'top',
    transition: 'none',
  },
];

export default {
  name: 'AnimatedLogoLoader',
  props: {
    size: {
      type: String,
      default: 'default', // 'default' or 'small'
    },
    hideText: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.animate();
    });
  },
  beforeDestroy() {
    this.isAnimating = false; // Stop animation on component destroy
  },
  data() {
    return {
      isAnimating: true,
      tempo: 0.5,
      pieceStyles: [...initialPieceStyles],
    };
  },
  methods: {
    wait(duration) {
      return new Promise(resolve => setTimeout(resolve, duration * 1000));
    },
    frame(callback) {
      return new Promise(resolve => {
        requestAnimationFrame(() => {
          callback();
          resolve();
        });
      });
    },
    async animate() {
      // RESET
      await this.frame(() => {
        this.pieceStyles.forEach((_, index) => {
          this.pieceStyles[index] = { ...initialPieceStyles[index] };
        });
      });
      // PIECE 1
      await this.frame(async () => {
        this.pieceStyles[0].opacity = '1';
        this.pieceStyles[0].transform = 'scaleY(1)';
        this.pieceStyles[0].transition = `transform ${this.tempo}s, opacity ${this.tempo}s`;
      });
      await this.wait(this.tempo);
      // PIECE 2
      await this.frame(() => {
        this.pieceStyles[1].opacity = '1';
        this.pieceStyles[1].transform = 'scaleX(1)';
        this.pieceStyles[1].transition = `transform ${this.tempo}s, opacity ${this.tempo}s`;
      });
      await this.wait(this.tempo);
      // PIECE 3
      await this.frame(() => {
        this.pieceStyles[2].opacity = '1';
        this.pieceStyles[2].transform = 'scaleY(1)';
        this.pieceStyles[2].transition = `transform ${this.tempo}s, opacity ${this.tempo}s`;
      });
      await this.wait(this.tempo);
      // FADE OUT ALL
      await this.frame(() => {
        this.pieceStyles.forEach(style => {
          style.transition = `opacity ${this.tempo}s`;
          style.opacity = '0';
        });
      });
      await this.wait(this.tempo);
      // REPEAT IF STILL ANIMATING
      if (this.isAnimating) {
        this.animate();
      }
    },
  },
};
</script>

<style scoped>
.root {
  display: inline-flex;
  flex-direction: column;
  container-type: inline-size;
  gap: 2cqw;
  width: 100px;
}

.root--small {
  max-width: 40px;
  gap: 1cqw;
}

.square {
  aspect-ratio: 200 / 192;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 10%;
  position: relative;
}

.area {
  position: relative;
}

.area--double {
  grid-row: span 2;
}

.piece {
  position: absolute;
  width: 100%;
  height: 100%;

  transition: 0.5s linear;
  transform: scaleY(1);
  opacity: 1;
}

.piece--ghost {
  z-index: 1;
  opacity: 0.4;
}

.piece--whole {
  z-index: 2;
}

.piece--black {
  background-color: #202228;
}

.piece--gold {
  background-color: #957610;
}

.text {
  color: #202228;
  width: 100%;
  text-align: center;
  font-size: 16cqw;
  pointer-events: none;
  user-select: none;
  padding-left: 0.4em;
}
</style>
